import { Anchor, Breadcrumbs, Text } from '@mantine/core';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import AiDraftNotice from '../../components/AiDrafts/AiDraftNotice';
import CampaignForm from '../../components/Campaigns/CampaignForm';
import SenderVerificationAlert from '../../components/SenderVerificationAlert';
import ErrorMessage from '../../components/ErrorMessage';
import Metadata from '../../components/Metadata';
import Spinner from '../../components/Spinner';
import { updateFlowNodeElementId } from '../../constants/flowTypes';
import { CampaignTypes, Page } from '../../constants/pageTitles';
import { normalizeCampaignGoals } from '../../helpers/campaignGoals';
import { friendlyGoogleErrorMessage } from '../../helpers/googleErrorUtils';
import useFetch from '../../helpers/useFetch';

const page = `${Page['campaigns']}`;

const CampaignsDetailsPage = () => {
  const fetch = useFetch();
  const navigate = useNavigate();
  const location = useLocation();

  const [campaign, setCampaign] = useState();
  const [userError, setUserError] = useState();
  const [loadError, setLoadError] = useState();
  const [loading, setLoading] = useState(false);
  const { accountId, domainId, campaignId } = useParams();

  useEffect(() => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignId}`)
      .then(({ campaign }) => {
        // Parse the query parameters
        const queryParams = new URLSearchParams(location.search);

        // Get the value of the 'flowElementId and flowType' parameter
        const flowType = queryParams.get('flowType');
        const flowElementId = parseInt(queryParams.get('flowElementId'));

        if (flowElementId && flowType) {
          campaign.flow = updateFlowNodeElementId(campaign.flow, flowType, flowElementId);
        }

        setCampaign(normalizeCampaignGoals(campaign));
      })
      .catch(setLoadError);
  }, [fetch, accountId, domainId, campaignId, location]);

  const close = () => {
    navigate(location.pathname.replace(`/campaigns/${campaignId}/details`, `/campaigns?mode=${campaign.mode}`));
  };

  const update = (campaign, cb) => {
    setLoading(true);
    setUserError(undefined);
    const campaignUpdate = { ...campaign };
    delete campaignUpdate.running;

    return fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignUpdate.id}`, {
      method: 'PUT',
      body: JSON.stringify(campaignUpdate),
    })
      .then(cb)
      .catch((error) => {
        setUserError({ message: friendlyGoogleErrorMessage(error?.message) });
        fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaignUpdate.id}`)
          .then(({ campaign }) => setCampaign(normalizeCampaignGoals(campaign)))
          .catch(() => {});
      })
      .finally(() => setLoading(false));
  };

  const del = (cb) => {
    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/campaigns/${campaign.id}`, { method: 'DELETE' })
      .then(() => {
        if (typeof cb === 'function') {
          cb();
        }
        navigate(location.pathname.replace(`/campaigns/${campaign.id}/details`, `/campaigns?mode=${campaign.mode}`));
      })
      .catch(() => {});
  };

  if (loadError) {
    return <ErrorMessage message={loadError.message} />;
  }

  if (campaign === undefined) {
    return (
      <>
        <Spinner />
      </>
    );
  }

  const breadcrumbItems = [
    { title: 'Home', href: `#/accounts/${accountId}/domains/${domainId}/analytics?mode=${campaign.mode}` },
    { title: page, href: `#/accounts/${accountId}/domains/${domainId}/campaigns?mode=${campaign.mode}` },
    { title: campaign.name, href: `#${location.pathname.replace('/details', '/analytics')}?mode=${campaign.mode}` },
    { title: 'Edit' },
  ].map((item, index) =>
    item.href ? (
      <Anchor href={item.href} key={index}>
        {item.title}
      </Anchor>
    ) : (
      <Text key={index}>{item.title}</Text>
    )
  );

  return (
    <>
      <Metadata title={`${page} | ${CampaignTypes[campaign.mode]} | ${campaign.name} | Edit`} />
      <Breadcrumbs mb="md">{breadcrumbItems}</Breadcrumbs>

      <SenderVerificationAlert />
      {campaign.aiReviewStatus === 'pending' && <AiDraftNotice />}

      <CampaignForm
        key={campaign.updatedAt}
        loading={loading}
        campaign={campaign}
        close={close}
        saveCampaign={update}
        deleteCampaign={(cb) => del(cb)}
      />
      {userError ? <ErrorMessage message={userError.message} /> : null}
    </>
  );
};

export default CampaignsDetailsPage;
